/**
 * OrderConfirmDialog — modal shown after the user picks Entry/SL/Target via
 * the chart BUY/SHORT capture flow. Extracted from ChartsPage in Round 67a
 * so the soon-to-arrive Options Strategy tab (R67b) has room to grow.
 *
 * Round 67a additions vs the original inline dialog:
 *   • Trade Sizing strip — Segment / Mode / Investment / Leverage / Lot size /
 *     Auto qty / Required margin (with coverage bar) / Available margin
 *     (per-broker breakdown).
 *   • computeAutoQty is owned by the parent so this component stays a pure
 *     presentation layer — easier to swap into a tabbed shell later.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Radio, RadioGroup, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { brokerFundsService, type BrokerFunds } from '../../services/brokerFunds.service';
import { USD_TO_INR } from '../../utils/constants';
import StrategySuggestionsTab from './StrategySuggestionsTab';
import { effectiveSegment, isAutoSegment, SIZE_AS_LABEL } from '../../utils/sizeOrdersAs';

type Side = 'BUY' | 'SHORT';
type OrderType = 'LIMIT' | 'MARKET';

export interface OrderConfirmInstrumentDetail {
  lotSize: number;
  leverage: number;
  eqInvestmentAmount: number;
  futInvestmentAmount: number;
  optInvestmentAmountBuy: number;
  optInvestmentAmountSell: number;
  tradeSegment: string | null;
  tradeMode: string | null;
  instrumentType: string | null;
}

export interface OrderStageRow {
  index: number;
  price: number;
  qty: number;
  cumQty: number;
  avg: number;
  slLoss: number;
  tgtGain: number;
  rr: number | null;
}

export interface OrderConfirmProps {
  open: boolean;
  symbol: string;
  exchange: string;
  side: Side | null;
  /** Blended average entry across all legs (== entry 1 when single). Used for
   *  the price line + the Trade-Sizing margin estimate. */
  entry?: number;
  sl?: number;
  target?: number;
  /** Total quantity across all legs (sum of per-leg quantities). */
  quantity?: number;
  orderType: OrderType;
  submitting?: boolean;
  decimals: number;
  isDark: boolean;
  instrumentDetail: OrderConfirmInstrumentDetail | null;
  /** The symbol has listed options → offer the Strategy tab (Direct stays the default). */
  hasOptionChain?: boolean;
  /** Same Risk:Reward shape used today — represents the fully-loaded position. */
  riskReward: { risk: number; gain: number; rr: number | null } | null;
  warning: string | null;
  /** G7 — non-blocking informational note (e.g. "entry above LTP → queued as
   *  a Breakout STOP entry"). Unlike `warning`, it does NOT disable submit. */
  info?: string | null;
  // ── Multi-entry scale-in (Round 89) ──────────────────────────────────────
  /** Captured entry-level prices E1..EN. When length > 1, the dialog renders
   *  the per-leg list + per-stage SL/Profit table. */
  entries?: number[];
  /** Per-leg quantities aligned with `entries`. */
  quantities?: number[];
  /** Total the budget sized before splitting — when the per-entry minimum of 1 pushes the
   *  split above it, the dialog says so instead of silently placing more. */
  sizedTotal?: number;
  /** Per-stage SL-loss / Target-gain at each cumulative fill. */
  stages?: OrderStageRow[] | null;
  // ── Synthetic-symbol trigger arming ───────────────────────────────────────
  /** When set, this confirm ARMS a synthetic trigger (not a direct order). The
   *  dialog then shows a prominent PAPER/LIVE choice so the mode is a conscious
   *  decision at placement (the toolbar chip alone was easy to miss — trades
   *  silently armed PAPER). */
  synthMode?: 'PAPER' | 'LIVE';
  onChangeSynthMode?: (m: 'PAPER' | 'LIVE') => void;
  /** Normal (non-synthetic) charts: place the order LIVE (real broker order) or PAPER
   *  (simulated — Trade.isSimulation, filled and exited by the app at live prices). */
  tradeMode?: 'PAPER' | 'LIVE';
  onChangeTradeMode?: (m: 'PAPER' | 'LIVE') => void;
  onClose: () => void;
  /** For multi-entry, the value is the TOTAL quantity (parent re-splits). */
  onChangeQuantity: (q: number | undefined) => void;
  onChangeOrderType: (t: OrderType) => void;
  onSubmit: () => void;
}

const fmtInr = (n: number): string =>
  '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });


const OrderConfirmDialog: React.FC<OrderConfirmProps> = ({
  open, symbol, exchange, side, entry, sl, target, quantity, orderType,
  submitting, decimals, isDark, instrumentDetail, hasOptionChain, riskReward, warning, info,
  entries, quantities, sizedTotal, stages, synthMode, onChangeSynthMode, tradeMode, onChangeTradeMode,
  onClose, onChangeQuantity, onChangeOrderType, onSubmit,
}) => {
  const isMulti = !!entries && entries.length > 1;
  // One PAPER / LIVE chooser serves both: arming a synthetic trigger, or placing an order.
  const isTrigger = synthMode != null;
  const mode = synthMode ?? tradeMode;
  const onMode = isTrigger ? onChangeSynthMode : onChangeTradeMode;
  const [funds, setFunds] = useState<BrokerFunds | null>(null);
  const [fundsLoading, setFundsLoading] = useState(false);
  // Tab strip — shown when the symbol has LISTED OPTIONS (Delta BTC/ETH, NSE/BSE F&O,
  // MCX). It used to follow Trade segment = OPTIONS, a flag that said nothing about
  // whether options exist. Direct (trade the symbol itself) is always the default, so
  // a BUY on a chart behaves the same whether or not a chain exists.
  const isOptionsSeg = !!hasOptionChain;
  const [tab, setTab] = useState<'strategy' | 'direct'>('direct');
  useEffect(() => { if (open) setTab('direct'); }, [open]);

  // Pull broker funds when the dialog opens. The service does its own
  // 15s cache so rapid open/close cycles don't hammer the endpoint.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFundsLoading(true);
    brokerFundsService.getFunds().then(f => {
      if (cancelled) return;
      setFunds(f);
      setFundsLoading(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  // ── Investment amount + Required margin derivation ────────────────────
  const sizing = useMemo(() => {
    if (!instrumentDetail || !entry || !quantity || !side) return null;
    const isUsd = (exchange || '').toUpperCase() === 'DELTA';
    const usdToInr = isUsd ? USD_TO_INR : 1;
    const lot = instrumentDetail.lotSize > 0 ? instrumentDetail.lotSize : 1;
    const lev = instrumentDetail.leverage > 0 ? instrumentDetail.leverage : 1;
    // "Size orders as" — explicit Equity/Futures, else Auto from the exchange (server rule).
    const seg = effectiveSegment(exchange, instrumentDetail.instrumentType, instrumentDetail.tradeSegment);

    // Which budget applies to the current trade?
    const investedInr = seg === 'EQUITY' ? instrumentDetail.eqInvestmentAmount : instrumentDetail.futInvestmentAmount;

    // Required margin in INR. The notional is always quantity × lot ×
    // entry price (in INR after USD→INR conversion), divided by the leverage.
    const notionalInr = entry * quantity * lot * usdToInr;
    const requiredInr = notionalInr / lev;

    return { investedInr, requiredInr, lot, lev, seg, notionalInr };
  }, [instrumentDetail, entry, quantity, side, exchange]);

  const coveragePct = useMemo(() => {
    if (!sizing || !funds || funds.availableInr <= 0) return null;
    return (sizing.requiredInr / funds.availableInr) * 100;
  }, [sizing, funds]);

  const coverageColor = coveragePct == null ? '#9e9e9e'
    : coveragePct > 80 ? '#c62828'
    : coveragePct > 50 ? '#ef6c00'
    : '#2e7d32';

  const sideColor = side === 'BUY' ? '#2e7d32' : '#c62828';

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isOptionsSeg ? 'md' : 'xs'} fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper' } }}
      // L5 — explicit aria-labelledby/describedby + non-Enter-default action
      // so screen-reader users get the modal context and so the destructive
      // "Place order" button isn't triggered by an accidental Enter on the
      // quantity field. The Submit button keeps its own visible label.
      aria-labelledby="order-confirm-title"
      aria-describedby="order-confirm-body"
    >
      <DialogTitle id="order-confirm-title" sx={{
        fontWeight: 800, fontSize: '1rem',
        borderBottom: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
        color: sideColor,
      }}>
        Confirm {side} · {symbol}
      </DialogTitle>
      {isOptionsSeg && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
          aria-label="Order entry method"
          sx={{ minHeight: 36, borderBottom: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}` }}>
          <Tab value="strategy" label="Strategy" sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.8rem', fontWeight: 700 }} />
          <Tab value="direct"   label="Direct"   sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.8rem', fontWeight: 700 }} />
        </Tabs>
      )}
      <DialogContent id="order-confirm-body" sx={{ pt: 2, pb: 1 }}>
        {/* Strategy tab — only when segment is OPTIONS. Hidden otherwise. */}
        {isOptionsSeg && tab === 'strategy' && (
          <StrategySuggestionsTab
            open={open}
            underlyingSymbol={symbol}
            exchange={exchange}
            direction={side === 'BUY' ? 'LONG' : 'SHORT'}
            riskInr={riskReward ? Math.abs(riskReward.risk) : 0}
            availableMarginInr={funds?.availableInr ?? 0}
            isDark={isDark}
          />
        )}
        {(!isOptionsSeg || tab === 'direct') && (
          <>
        {/* ── Trade prices ──────────────────────────────────────────────── */}
        {isMulti ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, mb: 0.5 }}>
              SCALE-IN ENTRIES ({entries!.length})
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', columnGap: 1.5, rowGap: 0.3, fontSize: '0.8rem' }}>
              {entries!.map((p, i) => (
                <React.Fragment key={i}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Entry {i + 1}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{p.toFixed(decimals)}</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                    qty {quantities?.[i] ?? '—'}
                    {i === 0 && orderType === 'MARKET'
                      ? <Typography component="span" sx={{ ml: 0.6, fontSize: '0.68rem', color: '#ef6c00', fontStyle: 'italic' }}>· fires MARKET now</Typography>
                      : <Typography component="span" sx={{ ml: 0.6, fontSize: '0.68rem', color: 'text.secondary', fontStyle: 'italic' }}>· rests until hit</Typography>}
                  </Typography>
                </React.Fragment>
              ))}
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Stop-Loss</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#e53935' }}>{sl?.toFixed(decimals)}</Typography>
              <Box />
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Target</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e88e5' }}>{target?.toFixed(decimals)}</Typography>
              <Box />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.7, fontSize: '0.82rem', mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Entry</Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
              {entry?.toFixed(decimals)}
              {orderType === 'MARKET' && (
                <Typography component="span" sx={{ ml: 0.6, fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                  (MARKET — uses LTP at fill)
                </Typography>
              )}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Stop-Loss</Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#e53935' }}>{sl?.toFixed(decimals)}</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Target</Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e88e5' }}>{target?.toFixed(decimals)}</Typography>
          </Box>
        )}

        {/* ── Per-stage SL / Profit table (Round 89) ────────────────────────
            Shows what the loss (if SL hits) and profit (if Target hits) would
            be after each cumulative entry fill, using the blended average. */}
        {isMulti && stages && stages.length > 0 && (
          <Box sx={{
            mb: 1.5, p: 1, borderRadius: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
          }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, mb: 0.6 }}>
              IF FILLED THROUGH EACH LEVEL
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto auto', columnGap: 1.2, rowGap: 0.3, fontSize: '0.72rem', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 700 }}>Stage</Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 700, textAlign: 'right' }}>Cum qty</Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 700, textAlign: 'right' }}>Avg entry</Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 700, textAlign: 'right' }}>SL loss</Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 700, textAlign: 'right' }}>Tgt gain</Typography>
              {stages.map((s) => (
                <React.Fragment key={s.index}>
                  <Typography sx={{ fontSize: '0.72rem' }}>thru E{s.index}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', textAlign: 'right' }}>{s.cumQty}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', textAlign: 'right' }}>{s.avg.toFixed(decimals)}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', textAlign: 'right', color: '#e53935', fontWeight: 700 }}>{fmtInr(s.slLoss)}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', textAlign: 'right', color: '#2e7d32', fontWeight: 700 }}>{fmtInr(s.tgtGain)}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Typography sx={{ mt: 0.6, fontSize: '0.66rem', color: 'text.secondary', fontStyle: 'italic' }}>
              ISL sits below the last entry (above for shorts). Target hit at any stage exits fully and cancels remaining entries.
            </Typography>
          </Box>
        )}

        {/* ── Quantity + Order type ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', minWidth: 70 }}>{isMulti ? 'Total qty' : 'Quantity'}</Typography>
          <TextField
            size="small" type="number"
            value={quantity ?? ''}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              onChangeQuantity(Number.isFinite(v) && v > 0 ? v : undefined);
            }}
            inputProps={{ min: 1, style: { fontSize: '0.82rem', padding: '4px 8px' } }}
            sx={{ width: 110 }}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
            {isMulti ? 'auto-split across entries (last takes half)' : 'auto-computed; edit to override'}
          </Typography>
        </Box>
        {isMulti && sizedTotal != null && quantity != null && quantity > sizedTotal && (
          <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: '0.74rem' }} data-testid="split-oversize">
            Your budget sizes <b>{sizedTotal}</b>, but each of the {entries!.length} entries needs at least 1 — this places <b>{quantity}</b>
            {' '}({(quantity / Math.max(1, sizedTotal)).toFixed(1)}× the budget). Use fewer entries, raise the investment, or lower the total.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', minWidth: 70 }}>{isMulti ? 'Entry 1 type' : 'Order type'}</Typography>
          <RadioGroup row value={orderType}
            onChange={(_, v) => onChangeOrderType(v as OrderType)}>
            <FormControlLabel value="LIMIT" control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: '0.78rem' }}>LIMIT</Typography>} />
            <FormControlLabel value="MARKET" control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: '0.78rem' }}>MARKET</Typography>} />
          </RadioGroup>
        </Box>

        {/* ── Trade Sizing strip (Round 67a) ────────────────────────────── */}
        {instrumentDetail && (
          <Box sx={{
            mt: 1.5, p: 1, borderRadius: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
          }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, mb: 0.6 }}>
              TRADE SIZING
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 0.4, fontSize: '0.76rem' }}>
              <Box>
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>Sized as </Typography>
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {SIZE_AS_LABEL[effectiveSegment(exchange, instrumentDetail.instrumentType, instrumentDetail.tradeSegment)]}
                  {isAutoSegment(instrumentDetail.tradeSegment) ? ' (auto)' : ''}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>Mode </Typography>
                <Typography component="span" sx={{ fontWeight: 700 }}>{instrumentDetail.tradeMode || 'Naked'}</Typography>
              </Box>
              <Box>
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>Invested </Typography>
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {sizing && sizing.investedInr > 0 ? fmtInr(sizing.investedInr) : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>Leverage </Typography>
                <Typography component="span" sx={{ fontWeight: 700 }}>{instrumentDetail.leverage > 1 ? `${instrumentDetail.leverage}×` : '1×'}</Typography>
                <Typography component="span" sx={{ ml: 1.2, color: 'text.secondary', fontSize: '0.72rem' }}>Lot </Typography>
                <Typography component="span" sx={{ fontWeight: 700 }}>{instrumentDetail.lotSize}</Typography>
              </Box>
            </Box>

            {/* Required vs Available coverage bar */}
            {sizing && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', mb: 0.3 }}>
                  <Box>
                    <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>Required </Typography>
                    <Typography component="span" sx={{ fontWeight: 800, color: coverageColor }}>{fmtInr(sizing.requiredInr)}</Typography>
                    {coveragePct != null && (
                      <Typography component="span" sx={{ ml: 0.4, color: coverageColor, fontSize: '0.72rem', fontWeight: 700 }}>
                        ({coveragePct.toFixed(0)}%)
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>Available </Typography>
                    <Typography component="span" sx={{ fontWeight: 800 }}>
                      {funds ? fmtInr(funds.availableInr) : fundsLoading ? '…' : '—'}
                    </Typography>
                  </Box>
                </Box>
                {/* Bar */}
                <Box sx={{
                  width: '100%', height: 6, borderRadius: 3, overflow: 'hidden',
                  bgcolor: isDark ? '#0f1924' : '#eceff1',
                }}>
                  <Box sx={{
                    height: '100%',
                    width: `${Math.min(100, Math.max(2, coveragePct ?? 0))}%`,
                    bgcolor: coverageColor,
                    transition: 'width 250ms ease',
                  }} />
                </Box>
                {/* Per-broker breakdown */}
                {funds && (
                  <Box sx={{ mt: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Tooltip title={
                      funds.status.delta === 'ok' ? `Delta available: ${fmtInr(funds.byBroker.delta)}` :
                      funds.status.delta === 'no_credentials' ? 'Delta: no credentials configured' :
                      funds.errors.delta || 'Delta: fetch error'
                    }>
                      <span>Delta: {funds.status.delta === 'ok' ? fmtInr(funds.byBroker.delta) : funds.status.delta === 'no_credentials' ? '—' : '✗'}</span>
                    </Tooltip>
                    <Tooltip title={
                      funds.status.zerodha === 'ok' ? `Zerodha available: ${fmtInr(funds.byBroker.zerodha)}` :
                      funds.status.zerodha === 'no_credentials' ? 'Zerodha: no credentials configured' :
                      funds.status.zerodha === 'no_token' ? 'Zerodha: not connected (login required)' :
                      funds.errors.zerodha || 'Zerodha: fetch error'
                    }>
                      <span>Zerodha: {funds.status.zerodha === 'ok' ? fmtInr(funds.byBroker.zerodha) : funds.status.zerodha === 'no_token' ? '⇄' : funds.status.zerodha === 'no_credentials' ? '—' : '✗'}</span>
                    </Tooltip>
                    {funds.cached && (
                      <Tooltip title="Cached — refreshes every 15s">
                        <span style={{ opacity: 0.6 }}>·</span>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {coveragePct != null && coveragePct > 80 && (
                  <Typography sx={{ mt: 0.4, fontSize: '0.7rem', color: '#c62828', fontWeight: 700 }}>
                    ⚠ Required margin exceeds 80% of available funds.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {!isTrigger && mode === 'PAPER' && (
          <Box data-testid="paper-note" sx={{
            mt: 1, p: 1, borderRadius: 1,
            bgcolor: isDark ? 'rgba(144,164,174,0.14)' : 'rgba(84,110,122,0.08)',
            border: `1px solid ${isDark ? '#78909c' : '#546e7a'}`,
            fontSize: '0.76rem', color: isDark ? '#cfd8dc' : '#37474f',
          }}>
            <b>📝 PAPER — simulated.</b> {orderType === 'MARKET' ? 'Fills at the live price now' : 'Fills at the live price when it reaches your entry'};
            {' '}the app watches your stop and target. No broker order, no margin used, and it stays out of your real P&amp;L.
          </Box>
        )}

        {warning && (
          <Box sx={{
            mt: 1, p: 1, borderRadius: 1,
            bgcolor: isDark ? 'rgba(244,67,54,0.18)' : 'rgba(244,67,54,0.08)',
            border: '1px solid #c62828',
            fontSize: '0.78rem', color: '#c62828', fontWeight: 600,
          }}>
            ⚠ {warning} Cancel and re-capture in order: Entry → SL → Target.
          </Box>
        )}

        {info && !warning && (
          <Box sx={{
            mt: 1, p: 1, borderRadius: 1,
            bgcolor: isDark ? 'rgba(33,150,243,0.15)' : 'rgba(33,150,243,0.08)',
            border: '1px solid #1976d2',
            fontSize: '0.78rem', color: isDark ? '#90caf9' : '#1565c0', fontWeight: 600,
          }}>
            ℹ {info}
          </Box>
        )}

        {riskReward && (
          <Box sx={{
            mt: 1, p: 1, borderRadius: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
          }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.78rem' }}>
              <Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>SL Risk</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#e53935' }}>
                  {fmtInr(riskReward.risk)}
                  {riskReward.risk < 0 && <span style={{ fontSize: '0.7rem', marginLeft: 4, color: '#2e7d32' }}>🔒</span>}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Target Gain</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#2e7d32' }}>{fmtInr(riskReward.gain)}</Typography>
              </Box>
            </Box>
            {riskReward.rr != null && (
              <Box sx={{ mt: 0.6, fontSize: '0.78rem', color: 'text.secondary' }}>
                Risk : Reward ≈ <strong style={{ color: riskReward.rr >= 1.5 ? '#2e7d32' : riskReward.rr >= 1 ? '#ef6c00' : '#c62828' }}>
                  1 : {riskReward.rr.toFixed(2)}
                </strong>
              </Box>
            )}
          </Box>
        )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 1.2, flexWrap: 'wrap', rowGap: 1, borderTop: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}` }}>
        {/* The PAPER/LIVE decision is made HERE, in the confirm, not on an easy-to-miss
            toolbar chip. LIVE reads loud + red. Synthetic charts ARM a trigger ("Fires
            as"); normal charts PLACE the order ("Place as"). Hidden on the Strategy tab,
            whose baskets carry their own mode. */}
        {mode != null && onMode && (!isOptionsSeg || tab === 'direct') && (
          <Box data-testid="order-mode-chooser" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexBasis: '100%', ml: '0 !important' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: isDark ? '#90a4ae' : '#546e7a' }}>{isTrigger ? 'Fires as' : 'Place as'}</Typography>
            {(['PAPER', 'LIVE'] as const).map(m => (
              <Button key={m} size="small" variant={mode === m ? 'contained' : 'outlined'}
                data-testid={`order-mode-${m}`} aria-pressed={mode === m}
                onClick={() => onMode(m)}
                color={m === 'LIVE' ? 'error' : 'inherit'}
                sx={{ textTransform: 'none', fontWeight: 800, py: 0.2, px: 1.2, fontSize: '0.72rem',
                  ...(m === 'PAPER' && mode === m ? { bgcolor: '#546e7a', color: '#fff', '&:hover': { bgcolor: '#455a64' } } : {}) }}>
                {m === 'PAPER' ? 'PAPER (simulated)' : 'LIVE (real orders)'}
              </Button>
            ))}
          </Box>
        )}
        <Button onClick={onClose} disabled={!!submitting} size="small" sx={{ textTransform: 'none' }}>
          {isOptionsSeg && tab === 'strategy' ? 'Close' : 'Cancel'}
        </Button>
        {/* Submit only meaningful for the Direct tab — Strategy cards have
            their own Execute Basket buttons per row. */}
        {(!isOptionsSeg || tab === 'direct') && (
          <Button onClick={onSubmit} data-testid="order-confirm-submit"
            disabled={!!submitting || !quantity || quantity <= 0 || warning != null}
            variant="contained" size="small"
            sx={{
              textTransform: 'none', fontWeight: 700, color: '#fff',
              // LIVE = real money: red, whatever the side. PAPER = slate. No mode = side colour.
              bgcolor: mode === 'LIVE' ? '#b71c1c' : mode === 'PAPER' ? '#546e7a' : sideColor,
              '&:hover': { bgcolor: mode === 'LIVE' ? '#8e0000' : mode === 'PAPER' ? '#455a64' : side === 'BUY' ? '#1b5e20' : '#b71c1c' },
            }}
          >
            {submitting ? 'Placing...'
              : isTrigger ? `Arm ${synthMode} ${side} trigger${isMulti ? ` (${entries!.length} entries)` : ''}`
              : `Place ${mode ? mode + ' ' : ''}${side} ${isMulti ? `scale-in (${entries!.length})` : orderType}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrderConfirmDialog;
